import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'

/**
 * The reference's vue-popper, reduced to what the board uses: a popover below (or, when it
 * does not fit, above) its reference element, kept inside the table card, with the same DOM
 * the reference CSS targets — span > span > div.popper[x-placement] + .popper__arrow, then
 * the reference element.
 *
 * Positioned like popper.js: absolute inside its offset parent, moved by translate3d, the
 * arrow at the reference's centre. Outside clicks call `onDocumentClick` (the reference's
 * @documentClick) — never a click inside the popover or on the reference itself.
 */
export function Popper({
  boundaryId,
  reference,
  children,
  allowTop = false,
  onDocumentClick,
  extraClass = '',
  updateKey,
}: {
  boundaryId: string
  reference: ReactNode
  children: ReactNode
  /** The rebet popover may open upward; bet popovers always open downward. */
  allowTop?: boolean
  onDocumentClick: () => void
  extraClass?: string
  /** Re-measure when this changes (content height changes while open). */
  updateKey?: unknown
}) {
  const wrap = useRef<HTMLSpanElement>(null)
  const pop = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ x: number; y: number; arrow: number; placement: 'bottom' | 'top' } | null>(null)

  useLayoutEffect(() => {
    const p = pop.current
    const r = wrap.current?.lastElementChild
    if (!p || !r) return
    const parent = (p.offsetParent as HTMLElement | null) ?? document.body
    const pr = parent.getBoundingClientRect()
    const rr = r.getBoundingClientRect()
    const boundary = document.getElementById(boundaryId)?.getBoundingClientRect() ?? pr
    const w = p.offsetWidth
    const h = p.offsetHeight
    const pad = 5
    let left = rr.left + rr.width / 2 - w / 2
    left = Math.max(boundary.left + pad, Math.min(left, boundary.right - pad - w))
    const below = rr.bottom + 5 + h <= window.innerHeight
    const placement: 'bottom' | 'top' = allowTop && !below ? 'top' : 'bottom'
    // margin-top / margin-bottom: 5px come from the reference CSS per placement.
    const top = placement === 'bottom' ? rr.bottom : rr.top - h - 10
    setPos({
      x: Math.round(left - pr.left),
      y: Math.round(top - pr.top),
      arrow: Math.round(rr.left + rr.width / 2 - left - 5 - 5),
      placement,
    })
  }, [boundaryId, allowTop, updateKey])

  useLayoutEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && e.target instanceof Node && wrap.current.contains(e.target)) return
      onDocumentClick()
    }
    // Attached after the click that opened the popover has finished bubbling — attached
    // synchronously, that same click reaches the document and closes it again at once.
    const id = window.setTimeout(() => document.addEventListener('click', onDoc), 0)
    return () => {
      window.clearTimeout(id)
      document.removeEventListener('click', onDoc)
    }
  }, [onDocumentClick])

  return (
    <span ref={wrap}>
      <span>
        <div
          ref={pop}
          className={`popper ${extraClass}`.trim()}
          x-placement={pos?.placement ?? 'bottom'}
          style={{
            position: 'absolute',
            willChange: 'transform',
            top: 0,
            left: 0,
            transform: `translate3d(${pos?.x ?? 0}px, ${pos?.y ?? 0}px, 0px)`,
            visibility: pos ? undefined : 'hidden',
          }}
        >
          {children}
          <div x-arrow="" className="popper__arrow" style={{ left: `${pos?.arrow ?? 0}px` }} />
        </div>{' '}
      </span>
      {reference}
    </span>
  )
}

declare module 'react' {
  // Merges with React's own HTMLAttributes<T>; the parameter name must match theirs.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface HTMLAttributes<T> {
    'x-placement'?: string
    'x-arrow'?: string
  }
}
